import React, { useState } from 'react';
import { FormattedMessage, useIntl } from '../../util/reactIntl';
import { PrimaryButton } from '..';
import BusRequestForm from './BusRequestForm';

import css from './FindABusCta.module.css';

const HEADING_ID = 'FindABusCta-heading';
const OPEN_BUTTON_ID = 'FindABusCta-open';

/** The existing listing flow. The landing page already links here directly. */
const LIST_A_VEHICLE_PATH = '/l/new';

/**
 * "Need a Bus? We'll Help You Find It."
 *
 * The entry point for a buyer whose vehicle is not in the listings — which,
 * for a 54-passenger coach or a wheelchair-accessible school bus, is most of
 * the time. The primary action opens the request form rather than linking to a
 * search that will come back empty; the secondary sends a seller into the
 * listing flow that already exists.
 *
 * ## Sourcing, not dispatch
 *
 * The one line that must not be cut: this sources a vehicle to buy, over days
 * or weeks. It is not a charter booking and it is not emergency transport. A
 * community coordinator who reads "we'll help you find a bus" as "a bus is
 * coming" could be waiting on a vehicle during an evacuation. The distinction
 * is in the CTA itself, not buried in the form, because the misreading happens
 * before anyone clicks.
 *
 * ## The photograph
 *
 * `imageUrl` is empty by default and nothing here supplies a stock substitute.
 * Two motorcoach images were approved in principle but no file reached the
 * repository, and one carries another operator's lettering and USDOT number,
 * which would imply both an association and that the vehicle is for sale here.
 * The band is complete as type on a panel until an image is cleared.
 */
export default function FindABusCta({ imageUrl = null, imageAlt = '', className = null }) {
  const intl = useIntl();
  const [isFormOpen, setFormOpen] = useState(false);

  // Returning focus to the control that opened the modal is what keeps a
  // keyboard user from being dropped at the top of a very long landing page.
  //
  // By id rather than by ref: PrimaryButton is a plain function component and
  // does not forward refs, so a ref here would be silently dropped and the
  // focus restore would quietly do nothing. Button spreads its remaining props
  // onto the real <button>, so the id does land.
  const handleClose = () => {
    setFormOpen(false);
    const opener = typeof document !== 'undefined' && document.getElementById(OPEN_BUTTON_ID);
    if (opener) opener.focus();
  };

  return (
    <section
      className={className ? `${css.root} ${className}` : css.root}
      aria-labelledby={HEADING_ID}
    >
      {imageUrl ? (
        // Decorative when no alt text was given, so a screen reader is not read
        // a filename.
        <img
          className={css.image}
          src={imageUrl}
          alt={imageAlt}
          aria-hidden={imageAlt ? undefined : true}
          loading="lazy"
        />
      ) : null}

      <div className={css.content}>
        <p className={css.eyebrow}>
          <FormattedMessage id="FindABusCta.eyebrow" />
        </p>
        <h2 className={css.heading} id={HEADING_ID}>
          <FormattedMessage id="FindABusCta.heading" />
        </h2>
        <p className={css.body}>
          <FormattedMessage id="FindABusCta.body" />
        </p>

        <div className={css.actions}>
          <PrimaryButton
            className={css.primaryButton}
            type="button"
            onClick={() => setFormOpen(true)}
            id={OPEN_BUTTON_ID}
            data-testid="find-a-bus-cta"
          >
            <FormattedMessage id="FindABusCta.primaryButton" />
          </PrimaryButton>

          {/* A plain link, like every other route into the listing flow on this
              page. Styled as a secondary button so the two actions read as a
              pair without the seller path competing with the buyer path. */}
          <a className={css.secondaryButton} href={LIST_A_VEHICLE_PATH} data-testid="list-your-bus">
            {intl.formatMessage({ id: 'FindABusCta.secondaryButton' })}
          </a>
        </div>

        {/* Sourcing is not dispatch. Said here, before anyone clicks. */}
        <p className={css.note} data-testid="find-a-bus-scope-note">
          <FormattedMessage id="FindABusCta.notDispatch" />
        </p>
        <p className={css.note}>
          <FormattedMessage id="FindABusCta.note" />
        </p>
      </div>

      <BusRequestForm isOpen={isFormOpen} onClose={handleClose} />
    </section>
  );
}
